<template>
  <div class="mobile-page">
    <main class="mobile-shell">
      <section class="mobile-card">
        <FormHeader
          title="Create your fundraiser"
          subtitle="Set up your fundraiser in a few simple steps."
          :progress-value="progressPercentage"
          :progress-label="currentStepLabel"
          :home-href="webURL"
          login-href="/login"
        />

        <FormCard>
          <FormLegend text="Fundraiser details" />

          <div class="field-stack">
            <BaseInput
              id="email"
              v-model="form.email"
              label="Email"
              type="email"
              placeholder="name@example.com"
              autocomplete="email"
              :error-message="getEmailError()"
              @blur="markTouched('email')"
            />

            <p v-if="showEmailLoginLink()" class="field-action-link">
              <a href="/login">Log in</a>
            </p>

            <BaseInput
              id="title"
              v-model="form.title"
              label="Fundraiser title"
              type="text"
              placeholder="e.g. Help Brian pay school fees"
              :maxlength="34"
              :hint="!getTitleError() ? 'What is your fundraiser about?' : ''"
              :error-message="getTitleError()"
              @blur="markTouched('title')"
            />

            <div class="select-root">
              <BaseSelect
                id="category"
                :model-value="form.category"
                label="Fundraiser type"
                placeholder="Select a category"
                :options="options.fundraiserTypes"
                :open="ui.showTypeOptions"
                :warningMessage="getCategoryWarning()"
                :error-message="getCategoryError()"
                @toggle="actions.toggleTypeOptions"
                @select="actions.selectType"
                @blur="markTouched('category')"
              />
            </div>

            <div class="select-root">
              <AmountInput
                id="target"
                :model-value="form.target"
                label="Target amount"
                placeholder="100,000"
                :hint="
                  !getTargetError() ? 'Set the amount you aim to raise.' : ''
                "
                :error-message="getTargetError()"
                :currency="form.currency"
                :currencies="options.currencies"
                :currency-open="ui.showCurrencyOptions"
                @update:modelValue="actions.updateTargetAmount"
                @toggle-currency="actions.toggleCurrencyOptions"
                @select-currency="actions.selectCurrency"
                @blur="markTouched('target')"
              />
            </div>

            <div class="select-root">
              <BaseSelect
                id="duration"
                :model-value="form.duration"
                label="Duration"
                placeholder="Select duration"
                :options="options.durations"
                :open="ui.showDurationOptions"
                :error-message="getDurationError()"
                @toggle="actions.toggleDurationOptions"
                @select="actions.selectDuration"
                @blur="markTouched('duration')"
              />
            </div>

            <div class="optional-group">
              <button
                class="text-link"
                type="button"
                @click="actions.toggleReferral"
              >
                {{
                  ui.showReferral
                    ? 'Hide referral code'
                    : 'Have a referral code?'
                }}
              </button>

              <div v-if="ui.showReferral" class="referral-wrap">
                <BaseInput
                  id="referral"
                  v-model="form.referralCode"
                  label="Referral code"
                  type="text"
                  placeholder="Enter referral code"
                  :readonly="ui.referralLocked"
                  :hint="ui.referralLocked ? 'Referral code applied.' : ''"
                />
                <p class="optional-note">(optional)</p>
              </div>
            </div>
          </div>

          <div class="card-action-area">
            <PrimaryButton
              :disabled="asyncUi.checkingEmail"
              @click="handleContinue"
            >
              {{ asyncUi.checkingEmail ? 'Checking...' : 'Continue' }}
            </PrimaryButton>

            <InlineNotice icon="🛡" variant="privacy">
              Your personal details are handled securely.
              <a :href="webUrl + '/privacy-policy'">Read our Privacy Policy</a>.
            </InlineNotice>

            <InlineNotice variant="legal">
              By continuing, you agree to M-Changa’s
              <a :href="webUrl + '/terms-of-use'">Terms &amp; Conditions</a>.
            </InlineNotice>
          </div>
        </FormCard>
      </section>
    </main>
  </div>
</template>

<script>
import FormHeader from '@/components/registration/FormHeader.vue'
import FormCard from '@/components/registration/FormCard.vue'
import FormLegend from '@/components/registration/FormLegend.vue'
import BaseInput from '@/components/registration/BaseInput.vue'
import BaseSelect from '@/components/registration/BaseSelect.vue'
import AmountInput from '@/components/registration/AmountInput.vue'
import PrimaryButton from '@/components/registration/PrimaryButton.vue'
import InlineNotice from '@/components/registration/InlineNotice.vue'
import { useRegistrationForm } from '@/composables/useRegistrationForm'
import { useRouter } from 'vue-router'
import { reactive, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useHead } from '@vueuse/head'

export default {
  name: 'RegisterStep1',
  components: {
    FormHeader,
    FormCard,
    FormLegend,
    BaseInput,
    BaseSelect,
    AmountInput,
    PrimaryButton,
    InlineNotice,
  },

  setup() {
    useHead({
      title: 'Register Your Fundraiser | M-Changa Africa',
      meta: [
        {
          name: `description`,
          content:
            'M-Changa Africa: Register and Activate your fundraiser in three easy steps.',
        },
      ],
    })

    const router = useRouter()
    const store = useStore()
    const webUrl = process.env.VUE_APP_WEB_URL || 'https://www.mchanga.africa'

    const {
      form,
      ui,
      options,
      progressPercentage,
      currentStepLabel,
      validations,
      actions,
    } = useRegistrationForm()

    const validationUi = reactive({
      submitted: false,
      touched: {
        email: false,
        title: false,
        category: false,
        target: false,
        duration: false,
      },
    })

    const asyncUi = reactive({
      checkingEmail: false,
      emailExistsError: '',
    })

    function handleDocumentClick(event) {
      if (!event.target.closest('.select-root')) {
        actions.closeAllDropdowns()
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleDocumentClick)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleDocumentClick)
    })

    function showEmailLoginLink() {
      return (
        asyncUi.emailExistsError ===
        'An account with this email already exists.'
      )
    }

    async function checkEmailExists(email) {
      asyncUi.checkingEmail = true
      asyncUi.emailExistsError = ''

      try {
        const resp = await store.dispatch('getEmailData', { email: email })

        if (Number(resp?.id) >= 1) {
          asyncUi.emailExistsError =
            'An account with this email already exists.'
          return true
        }

        if (!resp || resp?.error) {
          asyncUi.emailExistsError =
            'We could not verify this email right now. Please try again.'
          return true
        }

        return false
      } catch (error) {
        asyncUi.emailExistsError =
          'We could not verify this email right now. Please try again.'
        return true
      } finally {
        asyncUi.checkingEmail = false
      }
    }

    function markTouched(field) {
      validationUi.touched[field] = true
    }

    function shouldShowError(field) {
      return validationUi.submitted || validationUi.touched[field]
    }

    function getEmailError() {
      if (asyncUi.emailExistsError) return asyncUi.emailExistsError
      return shouldShowError('email')
        ? validations.emailValidation.value.message
        : ''
    }

    function getTitleError() {
      return shouldShowError('title')
        ? validations.titleValidation.value.message
        : ''
    }

    function getCategoryError() {
      return shouldShowError('category')
        ? validations.categoryValidation.value.message
        : ''
    }

    function getCategoryWarning() {
      return shouldShowError('category')
        ? validations.categoryValidation.value.warning
        : ''
    }

    function getTargetError() {
      return shouldShowError('target')
        ? validations.amountValidation.value.message
        : ''
    }

    function getDurationError() {
      return shouldShowError('duration')
        ? validations.durationValidation.value.message
        : ''
    }

    async function handleContinue() {
      form.email = form.email.trim()
      form.title = form.title.trim()
      form.referralCode = form.referralCode.trim()

      validationUi.submitted = true
      asyncUi.emailExistsError = ''

      if (!validations.step1IsValid.value) return

      const exists = await checkEmailExists(form.email)
      if (exists) return

      actions.nextStep()
      router.push('/signup/step-2')
    }

    return {
      form,
      ui,
      options,
      progressPercentage,
      currentStepLabel,
      validations,
      actions,
      validationUi,
      asyncUi,
      webUrl,
      markTouched,
      getEmailError,
      getTitleError,
      getCategoryError,
      getCategoryWarning,
      getTargetError,
      getDurationError,
      showEmailLoginLink,
      handleContinue,
    }
  },
}
</script>

<style scoped>
.mobile-page {
  min-height: 100vh;
  padding: 16px;
  background: radial-gradient(
      circle at top left,
      rgba(136, 151, 57, 0.22),
      transparent 26%
    ),
    radial-gradient(
      circle at bottom right,
      rgba(252, 196, 21, 0.16),
      transparent 24%
    ),
    linear-gradient(180deg, #fafaf7 0%, #fafaf7 100%);
  font-family: Calibri, 'Segoe UI', sans-serif;
  color: #26311d;
}

.mobile-shell {
  max-width: 430px;
  margin: 0 auto;
}

.mobile-card {
  background: transparent;
  padding: 2px 0 10px;
}

.field-stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.optional-group {
  margin-top: -2px;
}

.text-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #445526;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
}

.referral-wrap {
  margin-top: 12px;
}

.optional-note {
  margin: 8px 0 0;
  font-size: 12px;
  color: #7b8474;
}

.card-action-area {
  margin-top: 20px;
  padding-top: 6px;
}

.field-action-link {
  text-align: right;
  font-size: 16px;
  line-height: 1.4;
}

.field-action-link a {
  color: #445526;
  font-weight: 600;
  text-decoration: underline;
}

.select-root {
  position: relative;
}

@media (min-width: 768px) {
  .mobile-page {
    padding: 32px 24px;
  }

  .mobile-shell {
    max-width: 640px;
  }

  .mobile-card {
    padding: 8px 0 20px;
  }

  .field-stack {
    gap: 20px;
  }

  .card-action-area {
    margin-top: 24px;
  }

  .field-action-link {
    margin-top: -8px;
  }
}
</style>
